/**
 * Parse a Cleartouch "Alert Open API" Postman v2.1.0 collection export into
 * the fields used by the Cleartouch source form.
 *
 * The export has two requests:
 *   - POST .../sendAlert   — headers carry x-open-app-key and x-open-token (start);
 *                            body carries { code, groupIds, fileId }
 *   - POST .../cancelAlert — same app key; x-open-token is the stop token;
 *                            body carries { code, groupIds }
 *
 * Pure function — no React / Chakra / toaster dependencies.
 */

export interface ParsedCleartouchPostman {
  site: {
    schoolCode?: string;
    appKey?: string;
    deviceGroups?: string[];
  };
  tokens: {
    startToken?: string;
    stopToken?: string;
    fileId?: string;
  };
  warnings: string[];
}

interface PostmanHeader {
  key: string;
  value: string;
}

interface PostmanBody {
  mode?: string;
  raw?: string;
}

interface PostmanUrl {
  raw?: string;
  path?: string[];
}

interface PostmanRequest {
  method?: string;
  header?: PostmanHeader[];
  body?: PostmanBody;
  url?: PostmanUrl | string;
}

interface PostmanItem {
  name?: string;
  request?: PostmanRequest;
}

interface PostmanCollection {
  info?: { schema?: string };
  item?: PostmanItem[];
}

const getHeader = (headers: PostmanHeader[] | undefined, key: string): string | undefined => {
  if (!headers) return undefined;
  const match = headers.find((h) => h.key?.toLowerCase() === key.toLowerCase());
  return match?.value;
};

const getUrlString = (url: PostmanRequest['url']): string => {
  if (!url) return '';
  if (typeof url === 'string') return url;
  if (url.raw) return url.raw;
  if (url.path) return url.path.join('/');
  return '';
};

interface ExtractedRequest {
  kind: 'send' | 'cancel';
  appKey?: string;
  token?: string;
  body: { code?: string; groupIds?: (string | number)[]; fileId?: string };
}

const extractRequest = (
  item: PostmanItem,
  warnings: string[],
): ExtractedRequest | null => {
  const req = item.request;
  if (!req) return null;
  const urlStr = getUrlString(req.url);
  let kind: 'send' | 'cancel' | null = null;
  if (/sendAlert/i.test(urlStr)) kind = 'send';
  else if (/cancelAlert/i.test(urlStr)) kind = 'cancel';
  if (!kind) {
    warnings.push(
      `Ignored unknown request "${item.name ?? urlStr}" — expected sendAlert or cancelAlert`,
    );
    return null;
  }

  const appKey = getHeader(req.header, 'x-open-app-key');
  const token = getHeader(req.header, 'x-open-token');

  let body: ExtractedRequest['body'] = {};
  if (req.body?.raw) {
    try {
      const parsed = JSON.parse(req.body.raw);
      body = {
        code: parsed.code != null ? String(parsed.code) : undefined,
        groupIds: Array.isArray(parsed.groupIds) ? parsed.groupIds : undefined,
        fileId: parsed.fileId != null ? String(parsed.fileId) : undefined,
      };
    } catch {
      warnings.push(`Could not parse request body for "${item.name ?? kind}"`);
    }
  }

  return { kind, appKey, token, body };
};

export function parseCleartouchPostman(raw: string): ParsedCleartouchPostman {
  let json: PostmanCollection;
  try {
    json = JSON.parse(raw);
  } catch {
    throw new Error('Not a valid JSON file');
  }

  if (!json?.info?.schema || !/postman\.com\/json\/collection/.test(json.info.schema)) {
    throw new Error('Not a Postman collection');
  }

  const warnings: string[] = [];
  const items = Array.isArray(json.item) ? json.item : [];
  const extracted = items
    .map((it) => extractRequest(it, warnings))
    .filter((r): r is ExtractedRequest => r !== null);

  const send = extracted.find((r) => r.kind === 'send');
  const cancel = extracted.find((r) => r.kind === 'cancel');

  if (!send && !cancel) {
    throw new Error('No sendAlert or cancelAlert request found in collection');
  }
  if (!send) warnings.push('No sendAlert request found — start token and file ID not imported');
  if (!cancel) warnings.push('No cancelAlert request found — stop token not imported');

  // Merge site-level values, preferring Send over Cancel. Warn on disagreement.
  const primary = send ?? cancel!;
  const secondary = send ? cancel : undefined;

  const schoolCode = primary.body.code ?? secondary?.body.code;
  if (send?.body.code && cancel?.body.code && send.body.code !== cancel.body.code) {
    warnings.push(
      `sendAlert code "${send.body.code}" differs from cancelAlert code "${cancel.body.code}" — using sendAlert`,
    );
  }

  const appKey = primary.appKey ?? secondary?.appKey;
  if (send?.appKey && cancel?.appKey && send.appKey !== cancel.appKey) {
    warnings.push('sendAlert and cancelAlert use different x-open-app-key values — using sendAlert');
  }

  const rawGroupIds = primary.body.groupIds ?? secondary?.body.groupIds;
  const deviceGroups = rawGroupIds?.map((g) => String(g));

  return {
    site: { schoolCode, appKey, deviceGroups },
    tokens: {
      startToken: send?.token,
      stopToken: cancel?.token,
      fileId: send?.body.fileId,
    },
    warnings,
  };
}
